import { useMemo, useState } from 'react'
import useBalances from './useBalances'
import { useTranslation } from 'react-i18next'
import { getIdentity } from '../handlers/walletActions/wallet'
import useWallet from './useWallet'
import { idlFactory as tcycleIdl } from '../idls/tokens/tcycle.did'
import type { _SERVICE as TCycleActor } from '../idls/tokens/tcycle.did'
import { Actor, ActorSubclass } from '@dfinity/agent'
import getAgent from '../utils/getAgent'
import { TCYCLES_CANISTER_ID } from '../constants/canisterIds'
import { Principal } from '@dfinity/principal'
import { decimalsToE8s } from '../utils/validationHelper'
import { TopUp } from '../clients/TopUp'
import { useGoogleAnalytics } from '../providers/GoogleAnalyticsProvider'
import { GAEventName } from '../interfaces/googleAnalytics'

const useCanisterManualTopup = () => {
  const [amount, setAmount] = useState<string>('')
  const [topupLoading, setTopupLoading] = useState(false)

  const { t } = useTranslation()

  const { findBalance, refreshBalance } = useBalances()
  const { wallet } = useWallet()
  const { fireEvent } = useGoogleAnalytics()

  const walletCyclesBalance = findBalance({ address: TCYCLES_CANISTER_ID })
  const tcyclesToken = walletCyclesBalance?.token

  const topUp = async (topupAmount: string, canisterId: string) => {
    if (!tcyclesToken) return

    setTopupLoading(true)

    const identity = await getIdentity(wallet.principalId)
    const agent = getAgent({ identity })

    const topupClient = new TopUp(agent)

    const amount = BigInt(decimalsToE8s(topupAmount || '', tcyclesToken.decimals))

    const actor = Actor.createActor(tcycleIdl, {
      agent,
      canisterId: TCYCLES_CANISTER_ID,
    }) as ActorSubclass<TCycleActor>

    try {
      const withdrawResponse = await actor.withdraw({
        to: Principal.from(canisterId),
        amount,
        from_subaccount: [],
        created_at_time: [],
      })

      if ('Ok' in withdrawResponse) {
        await topupClient.registerManualTopup(canisterId, amount)
        fireEvent(GAEventName.CYCLE_TOPUP, {
          amount: Number(amount),
        })

        return true
      }

      if ('Err' in withdrawResponse) {
        return Promise.reject(JSON.stringify(withdrawResponse.Err))
      }

      refreshBalance(tcyclesToken)

      return true
    } finally {
      setTopupLoading(false)
    }
  }

  const error = useMemo(() => {
    if (!tcyclesToken) return
    if (!Number(amount)) return t('send.errors.noAmount')

    const fee = (tcyclesToken.fee || 0) * 10 ** -tcyclesToken.decimals

    if (parseFloat(amount) + fee > walletCyclesBalance.balance) {
      return t('send.errors.lowBalance', { token: tcyclesToken.symbol })
    }
  }, [amount, walletCyclesBalance])

  return {
    amount,
    setAmount,
    walletCyclesBalance,
    topUp,
    error,
    topupLoading,
  }
}

export default useCanisterManualTopup
